import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Lightbulb, Lock, Plus, RefreshCw, TrendingUp } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { askUpgrade } from "@/components/plans/UpgradeDialog";
import { Button } from "@/components/ui/button";
import { Badge, Card } from "@/components/ui/misc";
import { Spinner } from "@/components/ui/spinner";
import { api } from "@/lib/api";
import { t } from "@shared/i18n";

type Tip = { title: string; body: string; category: string; source: "ai" | "library" };
type Idea = { title: string; why: string; firstStep: string; category: string; impact: number; effort: number };

/** Conseil du jour : personnalisé chaque matin (gratuit pour tous). */
export function DailyTipCard() {
  const tip = useQuery({ queryKey: ["growth", "tip"], queryFn: async () => (await api<{ data: Tip }>("/api/growth/tip")).data, staleTime: 3600_000 });
  return (
    <Card className="relative overflow-hidden p-5">
      <div className="pointer-events-none absolute -right-8 -top-8 size-28 rounded-full bg-brand/20 blur-2xl" aria-hidden />
      <p className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wide text-primary"><Lightbulb className="size-4" /> {t("Conseil du jour")}</p>
      {tip.isLoading ? <Spinner className="mt-3" /> : tip.data ? (
        <>
          <p className="mt-2 text-[16px] font-bold leading-snug">{tip.data.title}</p>
          <p className="mt-1.5 text-[14px] leading-relaxed text-muted">{tip.data.body}</p>
          <Badge className="mt-3">{tip.data.category}</Badge>
        </>
      ) : <p className="mt-2 text-sm text-muted">{t("Revenez demain pour un nouveau conseil.")}</p>}
    </Card>
  );
}

const dots = (n: number) => "●".repeat(n) + "○".repeat(5 - n);

/** Idées de croissance de la semaine, transformables en tâche (formule Essentiel et plus). */
export function GrowthIdeasCard() {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const ideas = useQuery({ queryKey: ["growth", "ideas"], queryFn: async () => (await api<{ data: { locked: boolean; ideas: Idea[] } }>("/api/growth/ideas")).data, staleTime: 3600_000, retry: false });
  const refresh = async () => {
    setRefreshing(true);
    try {
      qc.setQueryData(["growth", "ideas"], (await api<{ data: { locked: boolean; ideas: Idea[] } }>("/api/growth/ideas?refresh=1")).data);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  };
  const toTask = async (i: Idea) => {
    try {
      await api("/api/tasks", { method: "POST", json: { title: i.title, description: t("{0}\n\nPremière étape : {1}", i.why, i.firstStep), priority: Math.min(5, Math.max(1, i.impact)) } });
      await qc.invalidateQueries({ queryKey: ["tasks"] });
      toast.success(t("Ajoutée à vos tâches."));
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-[15px] font-semibold"><TrendingUp className="size-4 text-primary" /> {t("Idées de croissance de la semaine")}</p>
        {ideas.data && !ideas.data.locked && <Button size="icon" variant="ghost" onClick={refresh} loading={refreshing} aria-label={t("Nouvelles idées")}><RefreshCw className="size-4" /></Button>}
      </div>
      {ideas.isLoading ? (
        <Spinner className="mt-4" />
      ) : ideas.data?.locked ? (
        <div className="mt-3 rounded-2xl bg-surface p-4">
          <ul className="space-y-2 text-sm text-muted blur-[3px] select-none" aria-hidden>
            <li>{t("• Relancer les 3 devis en attente avec une offre limitée")}</li>
            <li>{t("• Lancer un parrainage auprès de vos meilleurs clients")}</li>
            <li>{t("• Créer une offre premium à +40 %")}</li>
          </ul>
          <p className="mt-3 flex items-center gap-2 text-[13px] font-medium"><Lock className="size-4 text-primary" /> {t("5 idées sur mesure chaque semaine, d'après votre activité.")}</p>
          <Button size="sm" className="mt-3" onClick={() => askUpgrade("growth_ideas", "starter", t("Chaque semaine, 5 idées concrètes pour gagner des clients et du chiffre d'affaires."))}>{t("Débloquer avec Essentiel")} <ArrowRight className="size-3.5" /></Button>
        </div>
      ) : ideas.isError ? (
        <p className="mt-3 text-sm text-muted">{(ideas.error as Error).message}</p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {ideas.data?.ideas.map((i) => (
            <li key={i.title} className="flex gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold">{i.title}</p>
                <p className="mt-0.5 text-[13px] text-muted">{i.why}</p>
                <p className="mt-1 text-[12px] text-subtle">{t("Impact")} <span className="text-primary">{dots(i.impact)}</span> {t("· Effort")} {dots(i.effort)} · {i.category}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => toTask(i)} aria-label={t("Ajouter « {0} » à mes tâches", i.title)}><Plus className="size-4" /></Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
